import { useState, useEffect } from "react";
import { Employee, EmployeeEvaluation } from "@/types/employee";
import { Criteria } from "@/types/criteria";
import Dashboard from "@/components/Dashboard";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2 } from "lucide-react";
import { exportAllEvaluationsToPDF } from "@/lib/pdfExport";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import EmployeeOwnDataCard from "@/components/EmployeeOwnDataCard";
import VehicleKilometerOverview from "@/components/VehicleKilometerOverview";

const DashboardPage = () => {
  const { user, userRole, profile } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [evaluations, setEvaluations] = useState<EmployeeEvaluation[]>([]);
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [loading, setLoading] = useState(true);
  
  const currentEmployeeId = profile?.employee_id || null;

  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Load employees from Supabase
        const { data: empData } = await supabase
          .from('employees')
          .select('*')
          .order('name');

        if (empData) {
          setEmployees(empData.map(emp => ({
            id: emp.id,
            name: emp.name,
            position: emp.position,
            employeeNumber: '',
            startDate: emp.created_at.split('T')[0]
          })));
        }

        // Load evaluations with all relations
        const { data: evalData } = await supabase
          .from('evaluations')
          .select(`
            *,
            criteria_ratings(
              *,
              rating_comments(
                *,
                created_by_profile:profiles!rating_comments_created_by_fkey(full_name)
              )
            )
          `)
          .order('date', { ascending: false });

        if (evalData) {
          const mappedEvaluations: EmployeeEvaluation[] = evalData.map(evaluation => {
            const criteriaObj: any = {};
            
            // Group ratings by criteria_key
            evaluation.criteria_ratings.forEach((cr: any) => {
              criteriaObj[cr.criteria_key] = {
                rating: cr.rating,
                points: cr.points,
                comments: cr.rating_comments.map((rc: any) => ({
                  text: rc.text,
                  date: rc.created_at,
                  userId: rc.created_by,
                  userName: rc.created_by_profile?.full_name || 'Unbekannt'
                }))
              };
            });

            return {
              id: evaluation.id,
              employeeId: evaluation.employee_id,
              month: evaluation.month,
              date: evaluation.date,
              totalScore: evaluation.total_score,
              maxScore: evaluation.max_score,
              performanceLevel: evaluation.performance_level,
              criteria: criteriaObj,
              createdBy: evaluation.created_by,
              createdByName: ''
            };
          });

          setEvaluations(mappedEvaluations);
        }

        // Load criteria from localStorage
        const storedCriteria = localStorage.getItem("criteria");
        if (storedCriteria) setCriteria(JSON.parse(storedCriteria));

        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();

    // Setup realtime subscription
    const channel = supabase
      .channel('dashboard-evaluations')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'evaluations' },
        () => loadData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleExportAll = () => {
    if (employees.length === 0) {
      toast.error("Keine Mitarbeiter vorhanden");
      return;
    }
    exportAllEvaluationsToPDF(employees, evaluations, criteria);
    toast.success("PDF wurde erstellt");
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Only admins and HR managers can access the dashboard
  const canAccessDashboard = userRole === 'admin' || userRole === 'hr_manager';
  
  if (!canAccessDashboard) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-foreground mb-2">Zugriff verweigert</h1>
        <p className="text-muted-foreground">Sie haben keine Berechtigung, das Dashboard anzuzeigen.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <header className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">Übersicht über alle Bewertungen und Statistiken</p>
      </header>

      <Dashboard 
        employees={employees} 
        evaluations={evaluations} 
        userRole={userRole}
        currentEmployeeId={currentEmployeeId}
      />
      
      {/* Kilometer-Übersicht */}
      <div className="mt-6">
        <VehicleKilometerOverview />
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={handleExportAll} className="gap-2">
          <FileDown className="h-4 w-4" />
          <span className="hidden sm:inline">Übersicht exportieren</span>
          <span className="sm:hidden">Export</span>
        </Button>
      </div>
    </div>
  );
};

export default DashboardPage;
